import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callOf } from "@/hooks/useBrokerageManage";
import {
  addShowcaseSite,
  confirmShowcaseCheckout,
  getPublicShowcase,
  getPublicShowcaseBySlug,
  getPublicShowcaseItem,
  getShowcase,
  openShowcasePortal,
  removeShowcaseSite,
  saveShowcaseItems,
  saveShowcaseSettings,
  sendShowcaseGuide,
  startShowcaseCheckout,
} from "@/services/showcaseService";
import { payloadOf } from "@/helpers/showcaseOrder";

export const showcaseKey = (id) => ["brokerageShowcase", id];

const isShowcasePayload = (data) => Boolean(data && data.showcase && Array.isArray(data.items));

export const useShowcase = (id) =>
  useQuery({
    queryKey: showcaseKey(id),
    queryFn: () => callOf(() => getShowcase(id), "Could not load your website showcase."),
    enabled: Boolean(id),
  });

const useOptimisticShowcase = (id, run, apply, fallback) => {
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: `showcase-${id}` },
    mutationFn: (input) => callOf(() => run(input), fallback),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: showcaseKey(id) });
      const previous = queryClient.getQueryData(showcaseKey(id));
      if (previous) queryClient.setQueryData(showcaseKey(id), apply(previous, input));
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(showcaseKey(id), context.previous);
    },
    onSuccess: (data) => {
      if (isShowcasePayload(data)) queryClient.setQueryData(showcaseKey(id), data);
    },
    onSettled: (data) => {
      if (!isShowcasePayload(data)) queryClient.invalidateQueries({ queryKey: showcaseKey(id) });
    },
  });
};

export const useSaveShowcaseItems = (id) =>
  useOptimisticShowcase(
    id,
    (items) => saveShowcaseItems(id, payloadOf(items)),
    (previous, items) => ({ ...previous, items }),
    "Your order did not save.",
  );

export const useSaveShowcaseSettings = (id) =>
  useOptimisticShowcase(
    id,
    (settings) => saveShowcaseSettings(id, settings),
    (previous, settings) => ({ ...previous, showcase: { ...previous.showcase, ...settings } }),
    "That setting did not save.",
  );

const useShowcaseMutation = (id, run, fallback) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => callOf(() => run(input), fallback),
    onSuccess: (data) => {
      if (isShowcasePayload(data)) queryClient.setQueryData(showcaseKey(id), data);
      else queryClient.invalidateQueries({ queryKey: showcaseKey(id) });
    },
  });
};

export const useAddShowcaseSite = (id) =>
  useShowcaseMutation(id, (host) => addShowcaseSite(id, host), "That website did not save.");

export const useRemoveShowcaseSite = (id) =>
  useShowcaseMutation(id, (siteId) => removeShowcaseSite(id, siteId), "That website could not be removed.");

export const useConfirmShowcaseCheckout = (id) =>
  useShowcaseMutation(id, (sessionId) => confirmShowcaseCheckout(id, sessionId), "We could not confirm your subscription.");

export const useShowcaseCheckout = (id) =>
  useMutation({
    mutationFn: () => callOf(() => startShowcaseCheckout(id), "Checkout did not open."),
  });

export const useShowcasePortal = (id) =>
  useMutation({
    mutationFn: () => callOf(() => openShowcasePortal(id), "Billing did not open."),
  });

export const useSendShowcaseGuide = (id) =>
  useMutation({
    mutationFn: (email) => callOf(() => sendShowcaseGuide(id, email), "The setup email did not send."),
  });

export const publicShowcaseKey = (by, value, sort, host) => ["publicShowcase", by, value, sort, host || ""];

export const usePublicShowcase = ({ showcaseKey: key, slug, sort, host }) => {
  const by = key ? "key" : "slug";
  const value = key || slug;
  return useInfiniteQuery({
    queryKey: publicShowcaseKey(by, value, sort, host),
    enabled: Boolean(value),
    retry: false,
    initialPageParam: 1,
    placeholderData: (previous) => previous,
    queryFn: ({ pageParam }) => {
      const params = { sort, page: pageParam, ...(host ? { host } : {}) };
      return callOf(
        () => (key ? getPublicShowcase(key, params) : getPublicShowcaseBySlug(slug, params)),
        "This showcase is unavailable.",
      );
    },
    getNextPageParam: (last, pages) => (last?.hasMore ? pages.length + 1 : undefined),
  });
};

export const usePublicShowcaseItem = (key, wantAdId) =>
  useQuery({
    queryKey: ["publicShowcaseItem", key, wantAdId],
    enabled: Boolean(key && wantAdId),
    retry: false,
    queryFn: () => callOf(() => getPublicShowcaseItem(key, wantAdId), "This Buyer Want-Listing is unavailable."),
  });
